import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'

import config from 'config'
import lang from '@/lang'
import { payKinds, RECEIPT_TYPES, receiptStates } from '@/modules/base/util/constants'
import { PaymentStateItem } from '@/modules/base/components/forms/payment-state'
import ThemeInterface from '@/theme-interface'
import ReceiptPaymentStateActions from '@/modules/receipt/form/payment-state/receipt-payment-state-actions'
import ReceiptStateBadge from '@/modules/receipt/form/receipt-state-badge'
import { DateInput } from '@/modules/base/components/inputs'
import moment from 'moment'
import { PAYMENT_TYPE_MORE_BOOK_LATER } from '@/modules/payment/constants'

const { PAID, ANALYZE_ERROR, TO_ANALYZE, IN_PROGRESS } = receiptStates
const notPayableStates = [PAID, ANALYZE_ERROR, TO_ANALYZE, IN_PROGRESS]
const { dateFormat: { api: apiDateFormat } } = config

const useStyles = makeStyles((theme: ThemeInterface) => ({
  wrapper: {
    width: '100%',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    display: 'flex',
    color: '#414a5f',
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.medium,
    marginRight: 15,
  },
  dateInput: {
    width: '50%',
  },
}))

interface ReceiptPaymentStateInterface {
  type: keyof typeof RECEIPT_TYPES,
  data: any,
  bookings: any[],
  readOnly: boolean,
  openPaymentDrawer: boolean,
  onAdd: (booking: any) => void,
  onAssign: (bookings: any[]) => void,
  onRemove: (tempId: string) => void,
  onUnlink: (id: string) => void,
  onPayDateChange: (value: string, name: string) => void,
  onDateValidationChange?: (isValid: boolean, name: string) => void,
  onRegisterDateValidation?: (name: string, validate: (() => boolean) | null) => void,
}

const ReceiptPaymentState = (props: ReceiptPaymentStateInterface) => {
  const classes = useStyles(props)
  const {
    type,
    bookings = [],
    openPaymentDrawer,
    readOnly,
    data,
    onAdd,
    onAssign,
    onRemove,
    onUnlink,
    onPayDateChange,
    onDateValidationChange,
    onRegisterDateValidation,
  } = props
  const { state, isDue, isOverDue, paidThrough, payDate, bankAccountName } = data
  const isDateEditable = paidThrough === payKinds.CASH || paidThrough === payKinds.OFFLINE

  const bookingEls = useMemo(() => {
    if (!bookings?.length) { return null }
    return bookings.map(booking => {
      const { id, bankAccountName, bookingDate, assignments, isNew, tempId, date } = booking
      return assignments.map((assignment, index) => {
        if (assignment.assignmentType === PAYMENT_TYPE_MORE_BOOK_LATER) {
          return null
        }

        const data = { bookingId: id, ...assignment, bankAccountName, bookingDate, isNew, tempId, date }
        return (
          <PaymentStateItem
            key={index}
            readOnly={readOnly}
            {...data}
            onRemove={onRemove}
            onUnlink={onUnlink} />
        )
      })
    })
  }, [bookings])

  const paymentActions = !notPayableStates.includes(state)
    ? (
      <ReceiptPaymentStateActions
        type={type}
        data={data}
        onAdd={onAdd}
        onAssign={onAssign}
        readOnly={readOnly}
        openPaymentDrawer={openPaymentDrawer} />
    )
    : null

  const mappedState = useMemo(() => {
    if (bookings.length && state !== receiptStates.DRAFT) {
      return state
    }

    if (!bookings.length && paidThrough) {
      return receiptStates.PAID
    }

    if (state === receiptStates.DRAFT) {
      return receiptStates.OPEN
    }

    if (isOverDue) {
      return receiptStates.ISOVERDUE
    }

    if (isDue) {
      return receiptStates.ISDUE
    }

    return state
  }, [state, isDue, isOverDue, bookings])

  if (bookings.length <= 0 && isDateEditable) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.titleWrapper}>
          <span className={classes.title}>{lang.paymentState.paidThrough(paidThrough, bankAccountName)}</span>
        </div>
        <div className={classes.dateInput}>
          <DateInput
            name='payDate'
            dataQsId='receiptPaymentState-payDate'
            value={payDate || moment().format(apiDateFormat)}
            label={lang.paymentState.payDate}
            disabled={readOnly}
            onChange={onPayDateChange}
            onValidationChange={onDateValidationChange}
            onRegisterValidation={onRegisterDateValidation} />
        </div>
      </div>
    )
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.titleWrapper}>
        <span className={classes.title}>{lang.receipt.payments.title}</span>
        <ReceiptStateBadge state={mappedState} />
      </div>
      {bookingEls}
      {paymentActions}
    </div>
  )
}

export default ReceiptPaymentState
